import React, { useState, useEffect } from 'react';
import { Modal, Form, Button, InputGroup } from 'react-bootstrap';
import { FaEnvelope, FaLock, FaShieldAlt, FaEye, FaEyeSlash } from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';
import './AuthModals.css';

export const LoginModal = ({ show, onHide, onLoginSuccess }) => {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [captcha, setCaptcha] = useState('');
  const [userCaptchaInput, setUserCaptchaInput] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  
  const mainColor = '#006186';

  useEffect(() => {
    if (show) generateCaptcha();
  }, [show]);

  const generateCaptcha = () => {
    const randomCode = Math.floor(1000 + Math.random() * 9000).toString();
    setCaptcha(randomCode);
    setUserCaptchaInput('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (userCaptchaInput !== captcha) {
      setError("Неверный код подтверждения");
      generateCaptcha();
      setLoading(false);
      return;
    }

    try {
      const response = await fetch('/api/auth/login', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email, password })
      });

      const data = await response.json();

      if (response.ok) {
        // Сохраняем токен
        localStorage.setItem('adminToken', data.token);
        localStorage.setItem('token', data.token);
        
        // Сохраняем данные пользователя из ответа
        const userDataFromLogin = {
          id: data.user.id,
          fio: data.user.fio,
          role: data.user.role || 'user',
          email: email
        };
        localStorage.setItem('adminUser', JSON.stringify(userDataFromLogin));
        
        // Закрываем модальное окно
        onHide();
        
        // Перенаправление в зависимости от роли
        if (data.user.role === 'admin') {
          window.location.href = '/admin';
        } else {
          window.location.href = '/profile';
        }
      } else {
        setError(data.error || 'Ошибка авторизации');
      }
    } catch (err) {
      console.error('Ошибка входа:', err);
      setError('Ошибка соединения с сервером');
    } finally {
      setLoading(false);
    }
  };

  const handleForgotPassword = (e) => {
    e.preventDefault();
    onHide();
    navigate('/forgot-password');
  };

  return (
    <Modal 
      show={show} 
      onHide={onHide} 
      centered 
      contentClassName="border-0 shadow-lg" 
      style={{ borderRadius: '12px' }}
    >
      <Modal.Header closeButton className="border-0 pb-0">
        <Modal.Title className="fw-bold w-100 text-center mt-3" style={{ color: '#1e293b', fontSize: '1.5rem' }}>
          Авторизация
        </Modal.Title>
      </Modal.Header>
      <Modal.Body className="px-4 pb-5">
        <div className="mx-auto mb-4" style={{ backgroundColor: mainColor, width: '40px', height: '3px', borderRadius: '2px' }}></div>

        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold text-muted text-uppercase">Электронная почта</Form.Label>
            <InputGroup>
              <InputGroup.Text className="bg-light border-end-0">
                <FaEnvelope className="text-muted" size={14} />
              </InputGroup.Text>
              <Form.Control
                type="email"
                placeholder="user@example.com"
                className="bg-light border-start-0 shadow-none py-2"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                disabled={loading}
              />
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-3">
            <Form.Label className="small fw-bold text-muted text-uppercase">Пароль</Form.Label>
            <InputGroup>
              <InputGroup.Text className="bg-light border-end-0">
                <FaLock className="text-muted" size={14} />
              </InputGroup.Text>
              <Form.Control
                type={showPassword ? 'text' : 'password'}
                placeholder="••••••••"
                className="bg-light border-start-0 shadow-none py-2"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
                disabled={loading}
              />
              <Button
                variant="outline-secondary"
                onClick={() => setShowPassword(!showPassword)}
                className="border-0 bg-light"
                disabled={loading}
                style={{ zIndex: 0 }}
              >
                {showPassword ? <FaEyeSlash className="text-muted" /> : <FaEye className="text-muted" />}
              </Button>
            </InputGroup>
          </Form.Group>

          <Form.Group className="mb-4">
            <Form.Label className="small fw-bold text-muted text-uppercase">Код подтверждения</Form.Label>
            <div className="d-flex gap-2 align-items-center mb-2">
              <div 
                className="captcha-display flex-grow-1 text-center py-2 fw-bold"
                style={{
                  backgroundColor: '#f8f9fa',
                  fontSize: '20px',
                  letterSpacing: '8px',
                  borderRadius: '6px',
                  border: `1px solid ${mainColor}`,
                  userSelect: 'none',
                  fontFamily: 'monospace',
                  color: mainColor
                }}
              >
                {captcha}
              </div>
              <Button 
                variant="outline-secondary" 
                size="sm" 
                onClick={generateCaptcha} 
                style={{ height: '42px', borderColor: '#cbd5e1', color: '#64748b' }}
                disabled={loading}
              >
                <FaShieldAlt size={14} />
              </Button>
            </div>
            <Form.Control
              type="text"
              placeholder="Введите код с изображения"
              className="bg-light shadow-none text-center py-2"
              value={userCaptchaInput}
              onChange={(e) => setUserCaptchaInput(e.target.value)}
              required
              maxLength="4"
              disabled={loading}
            />
          </Form.Group>

          {error && (
            <div className="text-center mb-3" style={{ color: '#dc2626', fontSize: '13px' }}>
              {error}
            </div>
          )}

          <Button
            variant="primary"
            type="submit"
            disabled={loading}
            className="w-100 py-2 fw-bold shadow-sm mb-4 border-0"
            style={{ backgroundColor: mainColor }}
          >
            {loading ? 'Вход...' : 'Войти в систему'}
          </Button>

          <div className="text-center">
            <a 
              href="#"
              onClick={handleForgotPassword}
              style={{ 
                color: mainColor, 
                fontSize: '13px',
                textDecoration: 'none',
                cursor: 'pointer'
              }}
              onMouseEnter={(e) => e.target.style.textDecoration = 'underline'}
              onMouseLeave={(e) => e.target.style.textDecoration = 'none'}
            >
              Забыли пароль?
            </a>
          </div>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export const ForgotPasswordModal = ({ show, onHide, onSwitchToLogin }) => {
  return null;
};